import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, ChevronRight } from 'lucide-react';

const LIFE_DAYS = 60;

function useLifeCountdown(startDate) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!startDate) return;
    const start = new Date(startDate);
    const unlock = new Date(start.getTime() + LIFE_DAYS * 24 * 60 * 60 * 1000);

    const tick = () => {
      const now = new Date();
      const diff = unlock - now;
      if (diff <= 0) { setTimeLeft(null); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startDate]);

  return timeLeft;
}

export { useLifeCountdown, LIFE_DAYS };

const PromptOriginLifeSection = ({ user }) => {
  const navigate = useNavigate();
  const timeLeft = useLifeCountdown(user?.life_countdown_start || user?.created_at);
  const isCounting = timeLeft !== null;

  const Digit = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold text-white"
        style={{
          background: 'rgba(95,46,255,0.18)',
          border: '1px solid rgba(138,77,255,0.25)',
          boxShadow: '0 0 20px rgba(95,46,255,0.12)',
        }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-[10px] text-slate-500 mt-1.5 uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="mb-6" data-testid="life-section">
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background:
            'linear-gradient(160deg, rgba(95,46,255,0.10) 0%, rgba(138,77,255,0.05) 50%, rgba(139,233,255,0.03) 100%)',
          border: '1px solid rgba(138,77,255,0.15)',
          boxShadow: '0 4px 30px rgba(95,46,255,0.08)',
        }}
      >
        <div className="px-5 pt-6 pb-2">
          <div className="flex items-center gap-3 mb-1">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #5F2EFF, #8A4DFF)',
                boxShadow: '0 4px 16px rgba(95,46,255,0.35)',
              }}
            >
              <Gem className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">PromptOrigin Life</h3>
              <p className="text-xs text-slate-500">Najwyższy poziom doświadczenia</p>
            </div>
          </div>
        </div>

        <div className="relative px-5 py-4">
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.06] pointer-events-none overflow-hidden">
            <svg width="280" height="100" viewBox="0 0 280 100" fill="none">
              <ellipse cx="110" cy="50" rx="40" ry="48" fill="url(#lifeGrad1)" />
              <ellipse cx="170" cy="50" rx="40" ry="48" fill="url(#lifeGrad2)" />
              <defs>
                <radialGradient id="lifeGrad1">
                  <stop stopColor="#8A4DFF" />
                  <stop offset="1" stopColor="transparent" />
                </radialGradient>
                <radialGradient id="lifeGrad2">
                  <stop stopColor="#8BE9FF" />
                  <stop offset="1" stopColor="transparent" />
                </radialGradient>
              </defs>
            </svg>
          </div>

          {isCounting ? (
            <div className="relative z-10">
              <p className="text-xs text-violet-300/70 uppercase tracking-widest mb-4 text-center font-medium">
                Przygotowujemy Twoje doświadczenie
              </p>
              <div className="flex justify-center gap-3 mb-4">
                <Digit value={timeLeft.days} label="dni" />
                <span className="text-violet-400/40 text-xl font-light self-start mt-3.5">:</span>
                <Digit value={timeLeft.hours} label="godz" />
                <span className="text-violet-400/40 text-xl font-light self-start mt-3.5">:</span>
                <Digit value={timeLeft.minutes} label="min" />
                <span className="text-violet-400/40 text-xl font-light self-start mt-3.5">:</span>
                <Digit value={timeLeft.seconds} label="sek" />
              </div>
              <p className="text-xs text-slate-500 text-center leading-relaxed max-w-[280px] mx-auto">
                Twój osobisty pomocnik AI jest w fazie przygotowania. Jakość wymaga czasu — każdy etap jest dopracowywany indywidualnie.
              </p>
            </div>
          ) : (
            <div className="relative z-10 text-center py-2">
              <p className="text-sm font-semibold text-violet-300 mb-1">Etap przygotowania zakończony</p>
              <p className="text-xs text-slate-500">PromptOrigin Life jest gotowe do aktywacji.</p>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 pt-2">
          <button
            onClick={() => navigate('/paywall')}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #5F2EFF, #8A4DFF)',
              boxShadow: '0 4px 20px rgba(95,46,255,0.25)',
            }}
            data-testid="life-cta"
          >
            Odkryj PromptOrigin Life
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptOriginLifeSection;
